import { useCallback, useMemo } from 'react';
import { SkippableFragmentType } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { playerSkipButtonActivatedV2 } from '@kinopoisk-int/evgen-web-events/web/player';
import {
    RemoteMode,
    SkipButtonActivation,
    SkipButtonType,
} from '@kinopoisk-int/evgen-web-events/web/named_enums';

import { useEventCallback } from '@/scenarios/legacy-player/hooks/analytics/use-event-callback';
import { PlayerAdapter } from '@/scenarios/legacy-player/types/instance';
import { usePlayerInstance } from '@/scenarios/legacy-player/lib/contexts/instance';

import { getOrientation } from '../../analytics/utils/get-orientations';

import { useSwitchToNextEpisode } from './use-switch-to-next-episode';

interface UIParams {
    instance: PlayerAdapter | null;
}

interface CallbackParams {
    skipSource: SkipButtonType;
    skippableFragmentType: SkipButtonActivation;
}

export const skippableFragmentTypeToSkipButtonTypeMap: Record<
    SkippableFragmentType,
    SkipButtonType
> = {
    PREROLL: SkipButtonType.Preroll,
    CREDITS: SkipButtonType.Credits,
    INTRO: SkipButtonType.Intro,
    RECAP: SkipButtonType.Recap,
    UNKNOWN: SkipButtonType.Credits,
};

type EventParams = Parameters<typeof playerSkipButtonActivatedV2>[1];

const buildEvgenEventParams: (params: {
    uiParams: UIParams;
    callbackParams: [CallbackParams];
}) => EventParams = ({
    uiParams: { instance },
    callbackParams: [{ skipSource, skippableFragmentType }],
}) => ({
    vsid: instance?.vsid ?? '',
    orientation: getOrientation(),
    remoteMode: RemoteMode.Disabled,
    activation: skippableFragmentType,
    type: skipSource,
});

export const useLogSkipButtonActivation = () => {
    const instance = usePlayerInstance();

    const uiParams = useMemo(
        () => ({
            instance,
        }),
        [instance]
    );

    return useEventCallback({
        event: playerSkipButtonActivatedV2,
        eventParamsBuilder: buildEvgenEventParams,
        uiParams,
    });
};

export const useSwitchToNextEpisodeWithAnalytics = (contentId: string) => {
    const switchToNextEpisodeWithoutAnalytics = useSwitchToNextEpisode(contentId);

    const handleSwitch = useLogSkipButtonActivation();

    const switchToNextEpisode = useCallback<typeof switchToNextEpisodeWithoutAnalytics>(
        (...args) => {
            const [skipSource] = args;
            const skippableFragmentType = args[1];

            switchToNextEpisodeWithoutAnalytics(...args);

            if (skippableFragmentType) {
                handleSwitch({
                    skipSource: skippableFragmentTypeToSkipButtonTypeMap[skippableFragmentType],
                    skippableFragmentType:
                        skipSource === 'NEXT_EPISODE_AUTO_SKIP'
                            ? SkipButtonActivation.ByTimer
                            : SkipButtonActivation.ByUser,
                });
            }
        },
        [handleSwitch, switchToNextEpisodeWithoutAnalytics]
    );

    return switchToNextEpisode;
};
